import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement, FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Hint, Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { OrderNotification } from "../SetupNotifications";
import { CustomerContactNotificationMethod } from "../../../TypeGen/customer-contact-notification-method";

type EditFormProps = {
  cancelEdit: () => void;
  onSubmit: (item: OrderNotification) => void;
  item: OrderNotification;
}

const checkCallOptions = [
  { text: 'None', value: 0 },
  { text: '15 Mins', value: 15 },
  { text: '30 Mins', value: 30 },
  { text: '1 Hour', value: 60 },
  { text: '2 Hours', value: 120 },
  { text: '4 Hours', value: 240 }
]

export const notificationMethodOptions = [
  { text: 'Auto Email', value: CustomerContactNotificationMethod.AutoEmail },
  { text: 'Phone', value: CustomerContactNotificationMethod.Phone },
  { text: 'Website', value: CustomerContactNotificationMethod.Website },
  { text: 'Manual Email', value: CustomerContactNotificationMethod.Email },
  { text: 'WhatsApp', value: CustomerContactNotificationMethod.WhatsApp }
]

const CustomDropDownList = (fieldRenderProps: FieldRenderProps) => {
  const { value, ...others } = fieldRenderProps;
  return (
    <DropDownList
      {...others}
      value={fieldRenderProps.data.find((x: any) => x[fieldRenderProps.dataItemKey] === value)}
      onChange={(e) => {
        fieldRenderProps.onChange({
          target: {
            value: e.target.value[fieldRenderProps.dataItemKey],
          },
        });
      }}
    />
  );
};

const WaitTimeCheckbox = (fieldRenderProps: FieldRenderProps) => {
  const { value, onChange, ...others } = fieldRenderProps;
  return (
    <Checkbox
      {...others}
      checked={value > 0}
      onChange={(e) => {
        onChange({
          target: {
            value: e.value ? 60 : 0,
          },
        });
      }}
    />
  );
};

const getNotificationLabel = (notificationMethod: CustomerContactNotificationMethod, base: string) => {
  if (notificationMethod === CustomerContactNotificationMethod.AutoEmail) {
    return `Email When ${base}`;
  }
  return `Create SE on ${base}`;
};

const getWaitTimeLabel = (notificationMethod: CustomerContactNotificationMethod, base: string) => {
  if (notificationMethod === CustomerContactNotificationMethod.AutoEmail) {
    return `Email If ${base}`;
  }

  return `WhatsApp If ${base}`;
};

const EditNotifications = (props: EditFormProps) => {
  const { cancelEdit, onSubmit, item, ...other } = props;
  const isNewItem = item.CustomerID === 0;
  return (
    <Form
      ignoreModified={isNewItem}
      initialValues={item}
      onSubmit={(data) => onSubmit(data as OrderNotification)}
      render={(renderProps) => {
        const notificationMethod = renderProps.valueGetter("NotificationMethod");
        const supportsWaitTimeNotifications =
          notificationMethod === CustomerContactNotificationMethod.AutoEmail ||
          notificationMethod === CustomerContactNotificationMethod.WhatsApp;
        return (
          <Dialog
            title={`${item.CustomerContactName} Notifications`}
            onClose={cancelEdit}
            className="dialog-w800"
          >
            <FormElement>
              {isNewItem && <FieldWrapper>
                <Label editorId="CustomerContactID">
                  Contact
                </Label>
                <Field
                  required
                  id="CustomerContactID"
                  name="CustomerContactID"
                  component={CustomDropDownList}
                  data={item.contacts}
                  textField="Name"
                  dataItemKey="ID"
                />
              </FieldWrapper>}
              <FieldWrapper>
                <Label editorId="NotificationMethod">
                  Notify By
                </Label>
                <Field
                  id="NotificationMethod"
                  name="NotificationMethod"
                  component={CustomDropDownList}
                  data={notificationMethodOptions}
                  textField="text"
                  dataItemKey="value"
                />
              </FieldWrapper>
              {(notificationMethod !== CustomerContactNotificationMethod.AutoEmail && notificationMethod !== CustomerContactNotificationMethod.WhatsApp) && <Hint>
                Selecting this option will create a Order Service Exception
              </Hint>}
              <FieldWrapper>
                <Label editorId="SendPositionNotificationMinuteInterval">
                  Check Call Interval
                </Label>
                <Field
                  required={item.Consolidated}
                  id="SendPositionNotificationMinuteInterval"
                  name="SendPositionNotificationMinuteInterval"
                  component={CustomDropDownList}
                  data={checkCallOptions}
                  textField="text"
                  dataItemKey="value"
                />
              </FieldWrapper>
              {notificationMethod === CustomerContactNotificationMethod.AutoEmail && <FieldWrapper>
                <Field
                  name="Consolidated"
                  component={Checkbox}
                  label="Consolidate All Position Update Emails by Check Call Interval"
                />
              </FieldWrapper>}
              <span className="k-form-separator" />
              <div className="row">
                <div className="col-md-6">
                  <FieldWrapper>
                    <Field
                      name="SendArrivedShipperNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Arrived Shipper")}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="SendLoadedNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Loaded")}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="SendArrivedConsigneeNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Arrived Consignee")}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="SendEmptiedNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Emptied")}
                    />
                  </FieldWrapper>
                </div>
                <div className="col-md-6">
                  <FieldWrapper>
                    <Field
                      name="SendArrivedBorderNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Arrived Border")}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="SendDepartedBorderNotification"
                      component={Checkbox}
                      label={getNotificationLabel(notificationMethod, "Departed Border")}
                    />
                  </FieldWrapper>
                  {notificationMethod !== CustomerContactNotificationMethod.AutoEmail && <FieldWrapper>
                    <Field
                      name="SendProofOfDelivery"
                      component={Checkbox}
                      label="Create SE: HC POD Needed"
                    />
                  </FieldWrapper>}
                </div>
              </div>
              {supportsWaitTimeNotifications && <>
                <span className="k-form-separator" />
                <div className="row">
                  <div className="col-md-6">
                    <FieldWrapper>
                      <Field
                        id="SendPickupWaitTimeMinutesNotification"
                        name="SendPickupWaitTimeMinutesNotification"
                        component={WaitTimeCheckbox}
                        label={getWaitTimeLabel(notificationMethod, "Pickup Wait Time Exceeds Free Time")}
                      />
                    </FieldWrapper>
                  </div>
                  <div className="col-md-6">
                    <FieldWrapper>
                      <Field
                        id="SendDeliveryWaitTimeMinutesNotification"
                        name="SendDeliveryWaitTimeMinutesNotification"
                        component={WaitTimeCheckbox}
                        label={getWaitTimeLabel(notificationMethod, "Delivery Wait Time Exceeds Free Time")}
                      />
                    </FieldWrapper>
                  </div>
                </div>
              </>}
              <span className="k-form-separator" />
              <div className="row">
                <div className="col-md-6">
                  <FieldWrapper>
                    <Field
                      name="OnlyIfAuthCompany"
                      component={Checkbox}
                      label="If Auth Company"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="OnlyIfAuthContact"
                      component={Checkbox}
                      label="Only If Auth Contact"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="OnlyIfBillTo"
                      component={Checkbox}
                      label="If Bill To Company"
                    />
                  </FieldWrapper>
                </div>
                <div className="col-md-6">
                  <FieldWrapper>
                    <Field
                      name="OnlyIfConsignee"
                      component={Checkbox}
                      label="If Consignee Company"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="OnlyIfShipper"
                      component={Checkbox}
                      label="If Shipper Company"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Field
                      name="OnlyIfLogistics"
                      component={Checkbox}
                      label="If Logistics Company"
                    />
                  </FieldWrapper>
                </div>
              </div>
            </FormElement>
            <DialogActionsBar layout="end">
              <Button
                onClick={cancelEdit}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                themeColor={isNewItem ? 'success' : 'primary'}
                disabled={!renderProps.allowSubmit}
                onClick={renderProps.onSubmit}
              >
                {isNewItem ? 'Create' : 'Update'}
              </Button>
            </DialogActionsBar>
          </Dialog>
        );
      }}
      {...other}
    />
  );
};
export default EditNotifications;
